"use client"
import React, { useState, useEffect, useMemo } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../Components/UI/dialog"
import { Button } from "../../Components/UI/button"
import { Input } from "../../Components/UI/input"
import { Label } from "../../Components/UI/label"
import { Textarea } from "../../Components/UI/textarea"
import { X, Upload, FileText, Check, Trash2, ArrowLeft, Plus } from "lucide-react"
import { useSelector } from 'react-redux'
import { currentUserHasPermission, getConnectionOptionsWithSearch, getMobileHeaderTitle } from '../../Utils/Utilities'
import { DatePicker } from '../../Components/Common/DatePicker'
import { format } from 'date-fns'
import BusinessInput from '../../Components/Common/BusinessInput'
import CustomSearchableDropdown from '../../Components/Common/CustomSearchableDD'
import { AlertBox } from "../../Components/Common/AlertBox"
import { toast } from 'sonner';
import apiService from "../../Services/APIService"
import useIsMobile from "../../Hooks/useIsMobile"
import MastersDialog from '../Settings/Masters'
import { manageContractAPI } from './ContractsAPI';
import { getAuthData } from "../../Utils/AuthUtils"
import ConnectionEntryDialog from '../Connections/ConnectionEntry'
import { useContractConnectionFilter } from '../../Hooks/useConnectionFilter'

// Reusable Tailwind classes
const contractCommonClasses = {
    spacing: "space-y-1.5",
    input: "h-11 text-sm transition-all duration-200 bg-white sahi-input",
    buttonPrimary: "sahi-primary-button",
    buttonSecondary: "sahi-secondary-button",
    gridCols1: "grid-cols-1",
    gridCols2: "grid-cols-1 sm:grid-cols-2",
    gridCols3: "grid-cols-1 sm:grid-cols-3",
    gap4: "gap-4",
    mb4: "mb-4",
    iconSize: "h-4 w-4",
    iconSizeMd: "h-5 w-5",
    iconSizeLg: "h-6 w-6",
    flexCol: "flex flex-col",
    flexRow: "flex flex-row"
};

const ContractEntry = ({
    isOpen,
    onClose,
    connID = null,
    companyName = "",
    prefilledData = {},
    contractID = null,
    onContractSaved = null,
    isRenewalMode = false,
    isCancellationMode = false
}) => {
    const isMobile = useIsMobile();

    const [formData, setFormData] = useState({
        connection: "",
        contactPerson: "",
        contactPersonDetails: null,
        contractNo: "",
        contractType: "",
        contractTypeID: 0,
        contractTypeSelection: "",
        product: "",
        startDate: format(new Date(), 'dd-MMM-yy'),
        startDateAPI: format(new Date(), 'yyyy-MM-dd'),
        expireDate: "",
        expireDateAPI: "",
        charges: "",
        renewal: "",
        notes: "",
        cancellationReason: "",
        file: null,
        originalFileName: "",
        followUps: []
    });

    const [uiStates, setUIStates] = useState({
        fileName: "",
        datePicker: {},
        deleteAlert: false,
        masterDialog: false,
        connectionDialog: false,
        connectionSearchText: '',
        masterSearchText: ''
    });

    const [masterDialogType, setMasterDialogType] = useState(null);
    const [selectedField, setSelectedField] = useState("");
    const [isFollowUpEnabled, setIsFollowUpEnabled] = useState(false);
    const [followUps, setFollowUps] = useState([]);
    const taskTypesData = useSelector((state) => state.masterData.recordTypes);
    const taskTypes = useMemo(() => {
        return taskTypesData.map((taskType) => ({
            label: taskType.Typ,
            value: taskType.Typ
        }));
    }, [taskTypesData]);

    // Redux selectors
    const connections = useSelector(state => state.connectionData.data || []);
    const canAddConnections = currentUserHasPermission('ConnEntry');
    const perm = currentUserHasPermission('CtrcActions');
    const canDltContracts = perm === 1

    const products = useSelector(state => state.masterData.products || []);

    const contractTypeOptions = useSelector(state => {
        const contractTypes = state.masterData.contractTypes || [];
        return contractTypes.map(type => ({
            value: type.Typ || type.TYP,
            label: type.Typ || type.TYP,
            id: type.CID
        }));
    })

    const getDialogTitle = () => {
        if (isRenewalMode) {
            const mobileTitle = getMobileHeaderTitle('Renew Contract');
            const desktopTitle = 'Renew Contract'
            return { mobileTitle, desktopTitle };
        }

        if (isCancellationMode) {
            const mobileTitle = getMobileHeaderTitle('Cancel Contract');
            const desktopTitle = 'Cancel Contract'
            return { mobileTitle, desktopTitle };
        }

        const baseTitle = contractID ? 'Edit ' : '';
        const suffix = contractID ? '' : ' Entry';
        const businessName = companyName || formData.connection || 'Loading...';

        // Mobile title - no business name
        const mobileTitle = getMobileHeaderTitle(`${baseTitle}Contract${suffix}`);

        // Desktop title - with business name when available
        const desktopTitle = (contractID || connID) && (companyName || formData.connection)
            ? `${baseTitle}Contract${suffix} - ${businessName}`
            : `${baseTitle}Contract${suffix}`;

        return { mobileTitle, desktopTitle };
    };

    // Process contact data from connection object
    const formatContactData = (contact, isPrimary = false) => ({
        CNm: contact.CNm || contact.ContactName,
        UserID: contact.UserID || contact.ContactUserID || contact.ContactID || 0,
        CC: contact.CC || 91,
        Mobile: contact.MNo || contact.Mobile || '',
        Email: contact.Email || contact.Eml || '',
        isPrimary
    });

    // Auto-select contact when connection changes
    const selectDefaultContact = (selectedConnection) => {
        let contacts = selectedConnection.Contacts;
        if (typeof contacts === 'string') {
            try { contacts = JSON.parse(contacts); } catch (e) { contacts = []; }
        }

        // Single contact - select it
        if (contacts && Array.isArray(contacts) && contacts.length === 1) {
            const contactDetails = formatContactData(contacts[0]);
            updateFormData({ contactPersonDetails: contactDetails });
            return contacts[0].CNm || '';
        }

        // Multiple contacts - leave empty for user selection
        if (contacts && Array.isArray(contacts) && contacts.length > 1) return '';

        // Default to primary contact
        const primaryContactDetails = formatContactData(selectedConnection);
        updateFormData({ contactPersonDetails: primaryContactDetails });
        return selectedConnection.ContactName || '';
    };

    // Unified form data updater
    const updateFormData = (updates) => {
        setFormData(prev => ({ ...prev, ...updates }));
    };

    // Handle business value input with validation
    const handleBusinessValueChange = (e) => {
        const value = e.target.value;
        const fieldName = e.target.name || e.target.id;

        if (value === '') {
            updateFormData({ [fieldName]: '' });
            return;
        }

        const numericValue = value.replace(/[^0-9.]/g, '');
        const parts = numericValue.split('.');
        if (parts.length > 2 || (parts[1] && parts[1].length > 2)) return;

        const numberValue = parseFloat(numericValue);
        if (!isNaN(numberValue) && numberValue > 999999999.99) return;

        updateFormData({ [fieldName]: numericValue });
    };

    // Handle date changes with business logic validation
    const handleDateChange = (fieldID, date) => {
        if (date) {
            const formattedDate = format(date, 'dd-MMM-yy');
            const apiDT = format(date, 'yyyy-MM-dd');

            // Clear expire date if start date is changed and expire date becomes invalid
            if (fieldID === 'startDate' && formData.expireDate) {
                const selectedStartDate = new Date(date);
                const currentExpireDate = new Date(formData.expireDate);
                selectedStartDate.setHours(0, 0, 0, 0);
                currentExpireDate.setHours(0, 0, 0, 0);

                if (currentExpireDate <= selectedStartDate) {
                    updateFormData({
                        [fieldID]: formattedDate,
                        [`${fieldID}API`]: apiDT,
                        expireDate: '',
                        expireDateAPI: ''
                    });

                    toast.info("Expire date cleared", {
                        description: "Expire date was cleared because it was on or before the new start date."
                    });
                    return;
                }
            }

            updateFormData({
                [fieldID]: formattedDate,
                [`${fieldID}API`]: apiDT
            });
        } else {
            updateFormData({
                [fieldID]: '',
                [`${fieldID}API`]: ''
            });
        }

        setUIStates(prev => ({
            ...prev,
            datePicker: { ...prev.datePicker, [fieldID]: false }
        }));
    };

    const handleSelectionChange = (fieldName, value, selectedConnection = null, searchText = null) => {
        if (fieldName === 'connection' && connID) return;

        if (value === 'add') {
            const masterTypeMap = {
                product: 'products'
            };

            if (masterTypeMap[fieldName]) {
                setSelectedField(fieldName);
                setMasterDialogType(masterTypeMap[fieldName]);
                // Store the search text for the master dialog
                setUIStates(prev => ({ ...prev, masterSearchText: searchText || '', masterDialog: true }));
            } else if (fieldName === 'connection') {
                // Store the search text for prefilling business name
                setUIStates(prev => ({
                    ...prev,
                    connectionSearchText: searchText || '',
                    connectionDialog: true
                }));
            }
            return;
        }

        if (fieldName === 'connection') {
            updateFormData({
                [fieldName]: value,
                contactPerson: selectedConnection ? selectDefaultContact(selectedConnection) : ''
            });
        } else if (fieldName === 'contactPerson') {
            const connectionToUse = connID
                ? connections.find(conn => conn.ConnID === connID)
                : connections.find(conn => conn.ConnID === formData.connection);

            if (connectionToUse) {
                const contactDetails = findContactInConnection(connectionToUse, value);
                updateFormData({
                    [fieldName]: value,
                    contactPersonDetails: contactDetails
                });
            } else {
                updateFormData({ [fieldName]: value });
            }
        } else if (fieldName === 'contractType') {
            const selectedContractType = contractTypeOptions && contractTypeOptions.length > 0
                ? contractTypeOptions.find(option => option && option.value === value)
                : null;
            updateFormData({
                contractType: value,
                contractTypeID: selectedContractType ? (selectedContractType.id || selectedContractType.CID || 0) : 0
            });
        } else {
            updateFormData({ [fieldName]: value });
        }
    };

    // Find specific contact in connection data
    const findContactInConnection = (connection, contactName) => {
        // Check primary contact
        if (connection.ContactName === contactName) {
            return formatContactData(connection, true);
        }

        // Check additional contacts
        let contacts = connection.Contacts;
        if (typeof contacts === 'string') {
            try { contacts = JSON.parse(contacts); } catch (e) { contacts = []; }
        }

        if (contacts && Array.isArray(contacts)) {
            const foundContact = contacts.find(contact => contact.CNm === contactName);
            return foundContact ? formatContactData(foundContact) : null;
        }

        return null;
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const allowedTypes = [
                "application/pdf",
                "image/jpeg",
                "image/jpg",
                "image/png",
                "image/webp"
            ];

            if (allowedTypes.includes(file.type)) {
                updateFormData({ file });
                setUIStates(prev => ({ ...prev, fileName: file.name }));
            } else {
                toast.error("Please select valid file format.", {
                    description: "Only PDF and image files (JPEG, PNG, WebP) are allowed"
                });
                event.target.value = "";
            }
        }
    };

    // Validate dates according to business rules
    const validateDates = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (formData.expireDate) {
            const expireDate = new Date(formData.expireDate);
            expireDate.setHours(0, 0, 0, 0);

            if (formData.startDate) {
                const startDate = new Date(formData.startDate);
                startDate.setHours(0, 0, 0, 0);
                if (expireDate <= startDate) {
                    toast.error("Please select valid expire date.", {
                        description: "Expire date must be after the start date."
                    });
                    return false;
                }
            }
        }

        return true;
    };

    // Handle form submission with comprehensive validation
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.connection && !connID) {
            toast.error("Please select a contact.");
            return;
        } else if (!formData.contractType && !isRenewalMode) {
            toast.error("Please select a contract type.");
            return;
        } else if (!formData.startDateAPI) {
            toast.error("Please select a valid start date.");
            return;
        } else if (!formData.expireDateAPI && !isCancellationMode) {
            toast.error("Please select a valid expire date.");
            return;
        } else if (isCancellationMode && !formData.cancellationReason.trim()) {
            toast.error("Please enter a cancellation reason.");
            return;
        }

        if (!validateDates()) return;
        if (!validateFollowUps()) return;

        try {
            const contactDetails = formData.contactPersonDetails || {};
            let uploadedFileName = '';

            // Handle PDF upload if file is selected
            if (formData.file) {
                try {
                    const uploadResult = await apiService.uploadFile(
                        formData.file,
                        "Contracts",
                        "both");

                    if (uploadResult.error) {
                        toast.error(uploadResult.message);
                        return;
                    }
                    uploadedFileName = uploadResult.data.relativePath;
                } catch (uploadError) {
                    console.error('PDF upload error:', uploadError);
                    toast.error('Failed to upload file.  Please ensure it is smaller than 2 MB and is of a valid format.');
                    return;
                }
            }

            // Determine final file name - use new upload, existing file, or empty
            let finalFileName = '';
            if (uploadedFileName) {
                // New file was uploaded
                finalFileName = uploadedFileName;
            } else if (formData.originalFileName && !formData.file) {
                // Keep existing file (no new file selected and original exists)
                finalFileName = prefilledData.fileName || formData.originalFileName;
            }

            const contractData = {
                contractID: contractID || prefilledData.contractID || 0,
                connID: connID || formData.connection,
                contractNo: formData.contractNo || "",
                contractType: formData.contractType || "",
                contractTypeID: formData.contractTypeID || 0,
                product: formData.product || "",
                startDate: formData.startDateAPI || null,
                expireDate: formData.expireDateAPI || null,
                charges: parseFloat(formData.charges) || 0,
                renewal: formData.renewal || "",
                contractFile: finalFileName,
                connContactName: formData.contactPerson || "",
                connContactID: contactDetails.UserID || 0,
                connCC: contactDetails.CC || 91,
                connMobile: contactDetails.Mobile || '',
                connEmail: contactDetails.Email || '',
                notes: formData.notes || "",
                isSeller: (formData.contractTypeSelection === 'purchase' || prefilledData?.contractTypeSelection === 'purchase') ? 0 : 1,
                followUps: isFollowUpEnabled && followUps.length > 0 ? followUps : []
            };

            const operation = isCancellationMode ? 'CANCEL' : (isRenewalMode ? 'RENEW' : (contractID ? 'EDIT' : 'ADD'));

            // Cancellation data for cancel operation
            if (isCancellationMode) {
                contractData.cancellationReason = formData.cancellationReason;
                contractData.cancelDate = format(new Date(), 'yyyy-MM-dd');
                // Get agency IDs from the contract data or use current agency as seller
                const authData = getAuthData();
                contractData.sellerAgencyID = prefilledData.sellerAgencyID || authData?.AgencyID || 0;
                contractData.buyerAgencyID = prefilledData.buyerAgencyID || 0;
            }

            await manageContractAPI(contractData, operation);

            toast.success(`Contract ${isCancellationMode ? "cancelled" : (isRenewalMode ? "renewed" : (contractID ? "updated" : "saved"))}.`);
            resetForm();
            if (onContractSaved) onContractSaved();
            onClose();
        } catch (error) {
            const errorMessage = error.isBusinessLogicError
                ? error.message
                : `Failed to ${contractID ? 'update' : 'save'} contract.`;

            toast.error(errorMessage);
        }
    };

    // Handle contract deletion
    const handleDeleteContract = async () => {
        try {
            const contactDetails = formData.contactPersonDetails || {};
            const deleteData = {
                contractID: contractID,
                connID: connID || formData.connection,
                contractNo: formData.contractNo || "",
                contractType: formData.contractType || "",
                contractTypeID: formData.contractTypeID || 0,
                product: formData.product || "",
                startDate: formData.startDateAPI || null,
                expireDate: formData.expireDateAPI || null,
                charges: parseFloat(formData.charges) || 0,
                renewal: formData.renewal || "",
                contractFile: prefilledData.fileName || '',
                connContactName: formData.contactPerson || "",
                connContactID: contactDetails.UserID || 0,
                connCC: contactDetails.CC || 91,
                connMobile: contactDetails.Mobile || '',
                connEmail: contactDetails.Email || '',
                notes: formData.notes || "",
                isSeller: (formData.contractTypeSelection === 'purchase' || prefilledData?.contractTypeSelection === 'purchase') ? 0 : 1
            };

            await manageContractAPI(deleteData, 'DELETE');
            toast.success('Contract deleted.');

            if (onContractSaved) onContractSaved();
            onClose();
        } catch (error) {
            const errorMessage = error.isBusinessLogicError
                ? error.message
                : 'Failed to delete contract.';
            toast.error(errorMessage);
        }
    };

    // Reset form to initial state
    const resetForm = () => {
        const today = new Date();
        setFormData({
            connection: connID || "",
            contactPerson: "",
            contactPersonDetails: null,
            contractNo: "",
            contractType: "",
            contractTypeID: 0,
            contractTypeSelection: "",
            product: "",
            charges: "",
            startDate: format(today, 'dd-MMM-yy'),
            startDateAPI: format(today, 'yyyy-MM-dd'),
            expireDate: "",
            expireDateAPI: "",
            renewal: "",
            notes: "",
            file: null,
            originalFileName: "",
            followUps: [],
        });
        setUIStates({
            fileName: "",
            datePicker: {},
            deleteAlert: false,
            masterDialog: false,
            connectionDialog: false,
            connectionSearchText: '',
            masterSearchText: ''
        });
        setFollowUps([]);
        setIsFollowUpEnabled(false);
    };

    // Handle successful connection creation or editing
    const handleConnectionAdded = (savedConnection) => {
        if (savedConnection) {
            updateFormData({
                connection: savedConnection.ConnID || savedConnection.connID,
                contactPerson: savedConnection.ContactName || savedConnection.name || ''
            });
        }
        // Clear the search text after connection is added
        setUIStates(prev => ({
            ...prev,
            connectionSearchText: '',
            connectionDialog: false
        }));
    };

    // Enhanced close handler
    const handleClose = () => {
        resetForm();
        onClose();
    };

    // Function to render disabled connection field in mobile
    const renderDisableConn = () => {
        const isShow = isMobile && connID && companyName;

        return isShow ? (
            <div key='connID' className="space-y-1.5">
                <Label htmlFor='connID' required={true}>
                    Contact
                </Label>
                <Input
                    id='connID'
                    name='connID'
                    value={companyName}
                    onChange={() => { }}
                    placeholder={'Contact'}
                    className="h-11 text-sm transition-all duration-200 bg-white sahi-input"
                    type={'text'}
                    autoComplete={'off'}
                    maxLength={50}
                    disabled={true}
                />
            </div>
        ) : null;
    };

    // Use the contract connection filter hook
    const contractTypeSelection = formData.contractTypeSelection || prefilledData?.contractTypeSelection;
    const { connectionOptions } = useContractConnectionFilter(connections, contractTypeSelection);

    const renderConnectionField = () => {
        if (connID) return null;

        // Transform connections data to include searchable text
        const filteredConnections = connections.filter(conn => !(conn.IsCold === 1 || conn.IsCold === true));
        const connectionOptions = getConnectionOptionsWithSearch(filteredConnections);

        return (
            <div className={contractCommonClasses.spacing}>
                <Label htmlFor="connection" required={true}>Contact</Label>
                <CustomSearchableDropdown
                    options={connectionOptions}
                    value={formData.connection}
                    onSelect={(value, option, searchText) => handleSelectionChange('connection', value, option?.connection, searchText)}
                    placeholder="e.g. John Infrastructure"
                    searchPlaceholder="Search by name, city, or person."
                    displayKey="label"
                    valueKey="value"
                    allowAdd={canAddConnections ? true : false}
                    addText="Add New Contact"
                    label="Contact"
                    emptyMessage="No contacts found."
                    enableMultiFieldSearch={true}
                />
            </div>
        );
    };

    // Render contact person field with smart options
    const renderContactPersonField = () => {
        const connectionToUse = connID
            ? connections.find(conn => conn.ConnID === connID)
            : connections.find(conn => conn.ConnID === formData.connection);

        if (!connectionToUse && !connID) {
            return (
                <div className={contractCommonClasses.spacing}>
                    <Label htmlFor="contactPerson" required={true}>Contact Person</Label>
                    <CustomSearchableDropdown
                        options={[]}
                        value=""
                        onSelect={() => { }}
                        placeholder="Select contact first"
                        disabled={true}
                        label={`Contact`}
                    />
                </div>
            );
        }

        if (!connectionToUse) {
            return (
                <div className={contractCommonClasses.spacing}>
                    <Label htmlFor="contactPerson" required={true}>Contact Person</Label>
                    <Input
                        id="contactPerson"
                        value={formData.contactPerson}
                        onChange={(e) => updateFormData({ contactPerson: e.target.value })}
                        placeholder="Enter contact person"
                        className={contractCommonClasses.input}
                    />
                </div>
            );
        }

        // Build contact options
        let contacts = connectionToUse.Contacts;
        if (typeof contacts === 'string') {
            try { contacts = JSON.parse(contacts); } catch (e) { contacts = []; }
        }

        let contactOptions = [];

        if (connectionToUse.ContactName) {
            contactOptions.push({
                value: connectionToUse.ContactName,
                label: connectionToUse.ContactName
            });
        }

        if (contacts && Array.isArray(contacts) && contacts.length > 0) {
            const additionalContacts = contacts.map(contact => ({
                value: contact.CNm,
                label: contact.CNm
            }));

            const allContacts = [...contactOptions, ...additionalContacts];
            contactOptions = allContacts.filter((contact, index, self) =>
                index === self.findIndex(c => c.value === contact.value)
            );
        }

        contactOptions.sort((a, b) => a.label.localeCompare(b.label));

        // Hide the field if there's only one option
        if (contactOptions.length === 1) {
            return null;
        }

        return (
            <div className={contractCommonClasses.spacing}>
                <Label htmlFor="contactPerson" required={true}>Contact Person</Label>
                <CustomSearchableDropdown
                    options={contactOptions}
                    value={formData.contactPerson}
                    onSelect={(value) => handleSelectionChange('contactPerson', value)}
                    placeholder="Select contact person"
                    searchPlaceholder="Search contacts..."
                    displayKey="label"
                    valueKey="value"
                    allowAdd={false}
                    label={`Contact`}
                    emptyMessage="No contacts found"
                />
            </div>
        );
    };

    // Render date field with business rules
    const renderDateField = (fieldID, label, placeholder, required = false) => {
        const dateValue = formData[fieldID] ? new Date(formData[fieldID]) : undefined;
        const isDatePickerOpen = uiStates.datePicker[fieldID] || false;
        const today = new Date();
        const currentYear = today.getFullYear();
        today.setHours(0, 0, 0, 0);

        let minDate, maxDate, restrictPastDates, defaultMonth;

        if (fieldID === 'startDate') {
            minDate = null;
            maxDate = null;
            restrictPastDates = null;

            defaultMonth = dateValue || today;
        } else if (fieldID === 'expireDate') {
            const startDate = formData.startDate ? new Date(formData.startDate) : null;

            if (startDate) {
                startDate.setHours(0, 0, 0, 0);
                // Add one day to start date for minimum expire date
                const minExpireDate = new Date(startDate);
                minExpireDate.setDate(minExpireDate.getDate() + 1);
                minDate = minExpireDate;
            } else {
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                minDate = tomorrow;
            }

            maxDate = null;
            restrictPastDates = (date) => {
                const dateOnly = new Date(date);
                dateOnly.setHours(0, 0, 0, 0);

                if (startDate) {
                    return dateOnly <= startDate;
                } else {
                    return dateOnly <= today;
                }
            };

            defaultMonth = dateValue || today;
        }

        return (
            <DatePicker
                key={fieldID}
                fieldID={fieldID}
                value={dateValue}
                onChange={(date) => handleDateChange(fieldID, date)}
                placeholder={placeholder}
                label={label}
                required={required}
                className={contractCommonClasses.spacing}
                disabled={restrictPastDates}
                minDate={minDate}
                maxDate={maxDate}
                defaultMonth={defaultMonth}
                onOpenChange={(open) => setUIStates(prev => ({
                    ...prev,
                    datePicker: { ...prev.datePicker, [fieldID]: open }
                }))}
                open={isDatePickerOpen}
                allowDeselect={false}
                showYearNavigation={true}
            />
        );
    };

    // Validate follow-up dates
    const validateFollowUps = () => {
        if (!isFollowUpEnabled || followUps.length === 0) return true;

        if (!formData.expireDateAPI) {
            toast.error("Please select an expiry date before adding follow-ups.");
            return false;
        }

        const expiryDate = new Date(formData.expireDateAPI);
        expiryDate.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let i = 0; i < followUps.length; i++) {
            const followUp = followUps[i];

            // Validate task type
            if (!followUp.taskType) {
                toast.error(`Please select a task type for follow-up #${i + 1}.`);
                return false;
            }

            // Validate based on dateType
            if (followUp.dateType === 'before') {
                if (!followUp.days || followUp.days === '' || isNaN(followUp.days) || followUp.days < 1) {
                    toast.error(`Please enter a valid number of days for follow-up #${i + 1}.`);
                    return false;
                }

                const followUpDate = new Date(expiryDate);
                followUpDate.setDate(followUpDate.getDate() - followUp.days);

                if (followUpDate < today) {
                    toast.error(`Follow-up #${i + 1} cannot be in the past. (${followUp.days} days before expiry)`);
                    return false;
                }
            } else if (followUp.dateType === 'on') {
                if (!followUp.tskDueDT) {
                    toast.error(`Please select a date for follow-up #${i + 1}.`);
                    return false;
                }

                const followUpDate = new Date(followUp.tskDueDT);
                followUpDate.setHours(0, 0, 0, 0);

                if (followUpDate < today) {
                    toast.error(`Follow-up #${i + 1} date cannot be in the past.`);
                    return false;
                }
            }
        }

        return true;
    };

    // Render Follow-up Section
    // Render Follow-up Section
    const renderFollowUpSection = () => {
        // Don't show if in renewal mode or if contract is already renewed
        if (isRenewalMode || prefilledData?.isRenewed) return null;

        const handleAddFollowUp = () => {
            // Check if last entry has all required fields
            if (followUps.length > 0) {
                const lastEntry = followUps[followUps.length - 1];
                if (!lastEntry.taskType) {
                    toast.error(`Please select task type for follow-up #${followUps.length}.`);
                    return;
                }
                if (lastEntry.dateType === 'before' && (!lastEntry.days || lastEntry.days === '' || isNaN(lastEntry.days) || lastEntry.days < 1)) {
                    toast.error(`Please add days for follow-up #${followUps.length}.`);
                    return;
                }
                if (lastEntry.dateType === 'on' && !lastEntry.tskDueDT) {
                    toast.error(`Please select a date for follow-up #${followUps.length}.`);
                    return;
                }
            }

            if (followUps.length < 3) {
                setFollowUps([...followUps, { taskType: '', dateType: 'before', days: '', tskDueDT: '' }]);
            }
        };

        const handleRemoveFollowUp = (index) => {
            const newFollowUps = followUps.filter((_, i) => i !== index);
            setFollowUps(newFollowUps);

            // If no follow-ups left, uncheck the checkbox
            if (newFollowUps.length === 0) {
                setIsFollowUpEnabled(false);
            }
        };

        const handleFollowUpChange = (index, field, value) => {
            if (field === 'days' && value && !/^\d+$/.test(value)) return;

            setFollowUps(prevFollowUps => {
                const newFollowUps = [...prevFollowUps];

                // Update the field
                newFollowUps[index] = {
                    ...newFollowUps[index],
                    [field]: value
                };

                // Clear opposite field when changing dateType
                if (field === 'dateType') {
                    if (value === 'before') {
                        newFollowUps[index].tskDueDT = '';
                    } else if (value === 'on') {
                        newFollowUps[index].days = '';
                    }
                }

                return newFollowUps;
            });
        };

        const handleCheckboxChange = (checked) => {
            setIsFollowUpEnabled(checked);
            if (checked && followUps.length === 0) {
                setFollowUps([{ taskType: '', dateType: 'before', days: '', tskDueDT: '' }]);
            } else if (!checked) {
                setFollowUps([]);
            }
        };

        // Get min and max dates for date picker based on contract dates
        const getDatePickerConstraints = () => {
            const startDate = formData.startDate ? new Date(formData.startDate) : null;
            const expireDate = formData.expireDate ? new Date(formData.expireDate) : null;

            if (startDate) startDate.setHours(0, 0, 0, 0);
            if (expireDate) expireDate.setHours(0, 0, 0, 0);

            return { startDate, expireDate };
        };

        return (
            <div className={contractCommonClasses.spacing}>
                {/* Checkbox */}
                <div className="flex items-center gap-2 mb-2">
                    <input
                        type="checkbox"
                        id="followUpCheckbox"
                        checked={isFollowUpEnabled}
                        onChange={(e) => handleCheckboxChange(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-sahi-primary-color focus:ring-sahi-primary-color cursor-pointer accent-sahi-primary-color"
                    />
                    <Label htmlFor="followUpCheckbox" className="cursor-pointer mb-0">
                        Add Follow-up
                    </Label>
                </div>

                {/* Follow-up Entries */}
                {isFollowUpEnabled && (
                    <div className="space-y-3">
                        {followUps.map((followUp, index) => {
                            const isDisabled = index < followUps.length - 1;
                            const { startDate, expireDate } = getDatePickerConstraints();

                            return (
                                <div key={index} className="space-y-1.5">
                                    {/* Desktop Layout - All in one line */}
                                    {!isMobile && (
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm text-gray-600 whitespace-nowrap">
                                                Follow-up by:
                                            </span>
                                            <div className="w-[130px]">
                                                <CustomSearchableDropdown
                                                    options={taskTypes}
                                                    value={followUp.taskType}
                                                    onSelect={(value) => handleFollowUpChange(index, 'taskType', value)}
                                                    placeholder="Select Type"
                                                    searchPlaceholder="Search types"
                                                    displayKey="label"
                                                    valueKey="value"
                                                    allowAdd={false}
                                                    label="Task Type"
                                                    emptyMessage="No task types."
                                                    disabled={isDisabled}
                                                />
                                            </div>
                                            {/* On Radio Option */}
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    id={`on-${index}`}
                                                    name={`dateType-${index}`}
                                                    value="on"
                                                    checked={followUp.dateType === 'on'}
                                                    onChange={(e) => handleFollowUpChange(index, 'dateType', e.target.value)}
                                                    className="h-4 w-4 cursor-pointer text-sahi-primary-color focus:ring-sahi-primary-color accent-sahi-primary-color"
                                                    disabled={isDisabled}
                                                />
                                                <Label htmlFor={`on-${index}`} className="cursor-pointer mb-0 text-sm whitespace-nowrap">
                                                    On
                                                </Label>
                                            </div>

                                            <div className="w-[140px]">
                                                <DatePicker
                                                    fieldID={`followUpDate-${index}`}
                                                    value={followUp.tskDueDT ? new Date(followUp.tskDueDT) : undefined}
                                                    onChange={(date) => handleFollowUpChange(index, 'tskDueDT', date ? format(date, 'dd-MMM-yy') : '')}
                                                    placeholder="20-Oct-25"
                                                    className="space-y-0"
                                                    disabled={(date) => {
                                                        const dateOnly = new Date(date);
                                                        dateOnly.setHours(0, 0, 0, 0);

                                                        // Disable if before start date or after expiry date
                                                        if (startDate && dateOnly < startDate) return true;
                                                        if (expireDate && dateOnly > expireDate) return true;

                                                        return isDisabled || followUp.dateType !== 'on';
                                                    }}
                                                    minDate={startDate}
                                                    maxDate={expireDate}
                                                    showYearNavigation={true}
                                                    isDisabled={isDisabled || followUp.dateType === 'before'}
                                                />
                                            </div>
                                            {/* Before Radio Option */}
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    id={`before-${index}`}
                                                    name={`dateType-${index}`}
                                                    value="before"
                                                    checked={followUp.dateType === 'before'}
                                                    onChange={(e) => handleFollowUpChange(index, 'dateType', e.target.value)}
                                                    className="h-4 w-4 cursor-pointer text-sahi-primary-color focus:ring-sahi-primary-color accent-sahi-primary-color"
                                                    disabled={isDisabled}
                                                />
                                                <Label htmlFor={`before-${index}`} className="cursor-pointer mb-0 text-sm whitespace-nowrap">
                                                    Before
                                                </Label>
                                            </div>

                                            <Input
                                                type="text"
                                                value={followUp.days}
                                                onChange={(e) => handleFollowUpChange(index, 'days', e.target.value)}
                                                placeholder="0"
                                                className="h-9 w-14 text-right text-sm"
                                                maxLength={3}
                                                disabled={isDisabled || followUp.dateType !== 'before'}
                                            />




                                            <span className="text-sm text-gray-600 whitespace-nowrap">
                                                days of expiry.
                                            </span>

                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 w-8 p-0 hover:bg-red-50 flex-shrink-0"
                                                onClick={() => handleRemoveFollowUp(index)}
                                            >
                                                <Trash2 className="h-4 w-4 text-sahi-red" />
                                            </Button>
                                        </div>
                                    )}

                                    {/* Mobile Layout - Stacked */}
                                    {isMobile && (
                                        <div className="space-y-2">
                                            {/* Row 1: Label + Dropdown */}
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-gray-600 whitespace-nowrap">
                                                    Follow-up by:
                                                </span>
                                                <div className="flex-1">
                                                    <CustomSearchableDropdown
                                                        options={taskTypes}
                                                        value={followUp.taskType}
                                                        onSelect={(value) => handleFollowUpChange(index, 'taskType', value)}
                                                        placeholder="Select Type"
                                                        searchPlaceholder="Search types"
                                                        displayKey="label"
                                                        valueKey="value"
                                                        allowAdd={false}
                                                        label="Task Type"
                                                        emptyMessage="No task types."
                                                        disabled={isDisabled}
                                                    />
                                                </div>
                                            </div>

                                            {/* Row 2: Before radio + input and On radio + date picker */}
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="radio"
                                                        id={`on-mobile-${index}`}
                                                        name={`dateType-mobile-${index}`}
                                                        value="on"
                                                        checked={followUp.dateType === 'on'}
                                                        onChange={(e) => handleFollowUpChange(index, 'dateType', e.target.value)}
                                                        className="h-4 w-4 cursor-pointer text-sahi-primary-color focus:ring-sahi-primary-color accent-sahi-primary-color"
                                                        disabled={isDisabled}
                                                    />
                                                    <Label htmlFor={`on-mobile-${index}`} className="cursor-pointer mb-0 text-sm whitespace-nowrap">
                                                        On
                                                    </Label>
                                                </div>
                                                <div className="flex-1">
                                                    <DatePicker
                                                        fieldID={`followUpDate-mobile-${index}`}
                                                        value={followUp.tskDueDT ? new Date(followUp.tskDueDT) : undefined}
                                                        onChange={(date) => handleFollowUpChange(index, 'tskDueDT', date ? format(date, 'dd-MMM-yy') : '')}
                                                        placeholder="20-Oct-25"
                                                        className="space-y-0"
                                                        disabled={(date) => {
                                                            const dateOnly = new Date(date);
                                                            dateOnly.setHours(0, 0, 0, 0);

                                                            if (startDate && dateOnly < startDate) return true;
                                                            if (expireDate && dateOnly > expireDate) return true;

                                                            return isDisabled || followUp.dateType !== 'on';
                                                        }}
                                                        minDate={startDate}
                                                        maxDate={expireDate}
                                                        showYearNavigation={true}
                                                        isDisabled={isDisabled || followUp.dateType === 'before'}
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="radio"
                                                        id={`before-mobile-${index}`}
                                                        name={`dateType-mobile-${index}`}
                                                        value="before"
                                                        checked={followUp.dateType === 'before'}
                                                        onChange={(e) => handleFollowUpChange(index, 'dateType', e.target.value)}
                                                        className="h-4 w-4 cursor-pointer text-sahi-primary-color focus:ring-sahi-primary-color accent-sahi-primary-color"
                                                        disabled={isDisabled}
                                                    />
                                                    <Label htmlFor={`before-mobile-${index}`} className="cursor-pointer mb-0 text-sm whitespace-nowrap">
                                                        Before
                                                    </Label>
                                                </div>
                                                <Input
                                                    type="text"
                                                    value={followUp.days}
                                                    onChange={(e) => handleFollowUpChange(index, 'days', e.target.value)}
                                                    placeholder="0"
                                                    className="h-9 w-14 text-right text-sm"
                                                    maxLength={3}
                                                    disabled={isDisabled || followUp.dateType !== 'before'}
                                                />

                                            </div>

                                            {/* Row 3: days of expiry text + Delete button */}
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600">
                                                    days of expiry.
                                                </span>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 hover:bg-red-50 flex-shrink-0"
                                                    onClick={() => handleRemoveFollowUp(index)}
                                                >
                                                    <Trash2 className="h-4 w-4 text-sahi-red" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Add Another Button */}
                        {followUps.length < 3 && (
                            <button
                                type="button"
                                onClick={handleAddFollowUp}
                                className="text-sm text-sahi-primary-color hover:text-foreground underline decoration-1 underline-offset-4 transition-colors"
                            >
                                + Add Follow-up
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Initialize form with prefilled data
    useEffect(() => {
        if (prefilledData && Object.keys(prefilledData).length > 0) {
            const today = new Date();
            let updatedData = {
                startDate: format(today, 'dd-MMM-yy'),
                startDateAPI: format(today, 'yyyy-MM-dd'),
                ...prefilledData
            };

            // For renewal mode, set start date to contract's expiry date and clear expiry date
            if (isRenewalMode && prefilledData.expireDate) {
                try {
                    const contractExpiryDate = new Date(prefilledData.expireDate);
                    if (!isNaN(contractExpiryDate.getTime())) {
                        updatedData.startDate = format(contractExpiryDate, 'dd-MMM-yy');
                        updatedData.startDateAPI = format(contractExpiryDate, 'yyyy-MM-dd');
                        updatedData.expireDate = "";
                        updatedData.expireDateAPI = "";
                    }
                } catch (e) {
                    console.error('Error parsing contract expiry date:', e);
                }
            }

            if (prefilledData.startDate && !prefilledData.startDateAPI) {
                try {
                    const startDate = new Date(prefilledData.startDate);
                    if (!isNaN(startDate.getTime())) {
                        updatedData.startDateAPI = format(startDate, 'yyyy-MM-dd');
                    }
                } catch (e) {
                    console.error('Error parsing start date:', e);
                }
            }

            if (prefilledData.expireDate && !prefilledData.expireDateAPI) {
                try {
                    const expireDate = new Date(prefilledData.expireDate);
                    if (!isNaN(expireDate.getTime())) {
                        updatedData.expireDateAPI = format(expireDate, 'yyyy-MM-dd');
                    }
                } catch (e) {
                    console.error('Error parsing expire date:', e);
                }
            }

            // Set original filename for edit mode
            if (prefilledData.fileName) {
                updatedData.originalFileName = prefilledData.fileName;
                // Extract just the filename for display
                const displayFileName = prefilledData.fileName.includes('/')
                    ? prefilledData.fileName.split('/').pop()
                    : prefilledData.fileName;
                setUIStates(prev => ({ ...prev, fileName: displayFileName }));
            }

            if (prefilledData.followUps && Array.isArray(prefilledData.followUps) && prefilledData.followUps.length > 0) {
                // Transform old format to new format if needed
                const transformedFollowUps = prefilledData.followUps.map(f => {
                    if (typeof f === 'object') return f;
                    return { taskType: '', dateType: 'before', days: f.toString(), tskDueDT: '' };
                });
                setFollowUps(transformedFollowUps);
                setIsFollowUpEnabled(true);
            } else {
                setFollowUps([]);
                setIsFollowUpEnabled(false);
            }

            setFormData(prev => ({ ...prev, ...updatedData }));
        }
    }, [prefilledData]);

    // Auto-set contact person when connID is provided
    useEffect(() => {
        if (connID && connections.length > 0) {
            const connectionToUse = connections.find(conn => conn.ConnID === connID);
            if (connectionToUse && !formData.contactPerson) {
                const defaultContact = selectDefaultContact(connectionToUse);
                if (defaultContact) {
                    updateFormData({ contactPerson: defaultContact });
                }
            }
        }
    }, [connID, connections, formData.contactPerson]);

    // Handle contract type ID mapping when contractTypeOptions are loaded
    useEffect(() => {
        if (formData.contractType && contractTypeOptions.length > 0 && formData.contractTypeID === 0) {
            const selectedContractType = contractTypeOptions.find(option =>
                option && option.value === formData.contractType
            );
            if (selectedContractType) {
                updateFormData({
                    contractTypeID: selectedContractType.id || selectedContractType.CID || 0
                });
            }
        }
    }, [contractTypeOptions, formData.contractType, formData.contractTypeID]);

    return (
        <>
            <Dialog open={isOpen} onOpenChange={handleClose}>
                <DialogContent className={`flex flex-col w-full h-full sm:h-auto sm:rounded-lg rounded-none ${(isRenewalMode || isCancellationMode) ? 'sm:max-w-md' : 'sm:max-w-3xl'}`}>
                    <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                        <div className="sm:hidden flex items-center justify-between w-full h-8">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                    <ArrowLeft className={contractCommonClasses.iconSizeLg} />
                                    <span className="sr-only">Back</span>
                                </DialogClose>
                                <DialogTitle className="text-lg font-medium">
                                    <div className="truncate">
                                        {getDialogTitle().mobileTitle}
                                    </div>
                                </DialogTitle>
                            </div>
                            {/* Right Side */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {(contractID && !isCancellationMode && canDltContracts) && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 p-0 hover:bg-white/10 hover:text-white"
                                        onClick={() => setUIStates(prev => ({ ...prev, deleteAlert: true }))}
                                    >
                                        <Trash2 className={contractCommonClasses.iconSize} />
                                    </Button>
                                )}
                            </div>
                        </div>
                        <DialogTitle className="hidden sm:block text-lg md:text-xl">
                            {getDialogTitle().desktopTitle}
                        </DialogTitle>
                        <div className="hidden sm:flex items-center gap-2">
                            {(contractID && !isCancellationMode && canDltContracts) && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 p-0  sm:text-text-color sm:hover:bg-gray-200"
                                    onClick={() => setUIStates(prev => ({ ...prev, deleteAlert: true }))}
                                >
                                    <Trash2 className={contractCommonClasses.iconSize} />
                                </Button>
                            )}
                            <DialogClose className="flex-shrink-0" tabIndex={-1}>
                                <X className={contractCommonClasses.iconSizeMd} />
                                <span className="sr-only">Close</span>
                            </DialogClose>
                        </div>
                    </DialogHeader>

                    <div className="px-4 flex-1 scrollbar-no-arrows overflow-y-scroll sm:max-h-[85vh] sm:pb-0">
                        <div className="space-y-4 pb-6">
                            {/* Show only minimal fields in renewal mode */}
                            {isRenewalMode ? (
                                <>
                                    {/* Renewal Mode - Only Date Fields */}
                                    <div className={`grid ${contractCommonClasses.gridCols2} ${contractCommonClasses.gap4}`}>
                                        {renderDateField("startDate", "Start Date", "Select Start Date", true)}
                                        {renderDateField("expireDate", "Expiry Date", "Select Expiry Date", true)}
                                    </div>
                                </>
                            ) : isCancellationMode ? (
                                <>
                                    {/* Cancellation Mode - Only Cancellation Reason */}
                                    <div className={contractCommonClasses.spacing}>
                                        <Label htmlFor="cancellationReason" required={true}>Cancellation Reason</Label>
                                        <Textarea
                                            id="cancellationReason"
                                            value={formData.cancellationReason}
                                            onChange={(e) => updateFormData({ cancellationReason: e.target.value })}
                                            placeholder="e.g. Higher Renewal Charges"
                                            className="text-sm transition-all duration-200 bg-white sahi-input resize-none"
                                            rows={3}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    {renderDisableConn()}

                                    {/* Connection and Contact Row - Always maintain consistent grid */}
                                    {(() => {
                                        const connectionField = renderConnectionField();
                                        const contactPersonField = renderContactPersonField();

                                        // Always render if there's either a connection field OR a contact field
                                        if (!connectionField && !contactPersonField) return null;

                                        // If only contact person field exists (no connection field), show it on the left
                                        if (!connectionField && contactPersonField) {
                                            return (
                                                <div className={`grid ${contractCommonClasses.gridCols2} ${contractCommonClasses.gap4} ${contractCommonClasses.mb4}`}>
                                                    {contactPersonField}
                                                    <div className="hidden sm:block"></div>
                                                </div>
                                            );
                                        }

                                        // If both fields exist, show connection on left, contact on right
                                        return (
                                            <div className={`grid ${contractCommonClasses.gridCols2} ${contractCommonClasses.gap4} ${contractCommonClasses.mb4}`}>
                                                {connectionField || <div className="hidden sm:block"></div>}
                                                {contactPersonField || <div className="hidden sm:block"></div>}
                                            </div>
                                        );
                                    })()}

                                    {/* First Row: Contract No, Contract Type, Product */}
                                    <div className={`grid ${contractCommonClasses.gridCols3} ${contractCommonClasses.gap4}`}>
                                        <div className={contractCommonClasses.spacing}>
                                            <Label htmlFor="contractNo" required={false}>Ref No.</Label>
                                            <Input
                                                id="contractNo"
                                                value={formData.contractNo}
                                                onChange={(e) => updateFormData({ contractNo: e.target.value })}
                                                placeholder="e.g. CON-001"
                                                className={contractCommonClasses.input}
                                            />
                                        </div>

                                        <div className={contractCommonClasses.spacing}>
                                            <Label htmlFor="contractType" required={true}>Contract Type</Label>
                                            <CustomSearchableDropdown
                                                options={contractTypeOptions}
                                                value={formData.contractType}
                                                onSelect={(value) => handleSelectionChange('contractType', value)}
                                                placeholder="e.g. Subscription"
                                                searchPlaceholder="Search contract types."
                                                displayKey="label"
                                                valueKey="value"
                                                allowAdd={false}
                                                label="Contract Type"
                                                emptyMessage="No contract types found"
                                            />
                                        </div>
                                        <div className={contractCommonClasses.spacing}>
                                            <Label htmlFor="product">Offering</Label>
                                            {(prefilledData?.contractTypeSelection === 'purchase' || formData.contractTypeSelection === 'purchase') ? (
                                                <Input
                                                    id="product"
                                                    value={formData.product}
                                                    onChange={(e) => updateFormData({ product: e.target.value })}
                                                    placeholder="e.g. CRM Software"
                                                    className={contractCommonClasses.input}
                                                />
                                            ) : (
                                                <CustomSearchableDropdown
                                                    options={products
                                                        .map(product => ({
                                                            value: product.name || product.PNm,
                                                            label: product.name || product.PNm,
                                                            id: product.id || product.PID
                                                        }))
                                                        .sort((a, b) => a.label.localeCompare(b.label))}
                                                    value={formData.product}
                                                    onSelect={(value, option, searchText) => handleSelectionChange('product', value, option, searchText)}
                                                    placeholder="e.g. CRM Software"
                                                    searchPlaceholder="Search Offerings."
                                                    displayKey="label"
                                                    valueKey="value"
                                                    allowAdd={true}
                                                    label='Offering'
                                                    addText="Add Offering"
                                                    emptyMessage="No Offerings found"
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className={`grid grid-cols-2 sm:${contractCommonClasses.gridCols3} ${contractCommonClasses.gap4}`}>
                                        {renderDateField("startDate", "Start Date", "Select Start Date", true)}
                                        {renderDateField("expireDate", "Expiry Date", "Select Expiry Date", true)}

                                        <div className="col-span-2 sm:col-span-1">
                                            <div className={contractCommonClasses.spacing}>
                                                <Label htmlFor="file" className="text-sm font-medium">
                                                    File (PDF/Image)
                                                </Label>
                                                <div className="relative">
                                                    <Input
                                                        id="file"
                                                        type="file"
                                                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                                                        onChange={handleFileChange}
                                                        className="hidden"
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        className="w-full justify-start text-left h-11 px-3 sahi-input text-sm!"
                                                        onClick={() => document.getElementById("file").click()}
                                                    >
                                                        {uiStates.fileName ? (
                                                            <div className="flex items-center gap-2 truncate w-full">
                                                                <FileText className={`${contractCommonClasses.iconSize} text-sahi-blue flex-shrink-0`} />
                                                                <span className="truncate text-sm flex-1">{uiStates.fileName}</span>
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-4 w-4 p-0 flex-shrink-0 hover:bg-gray-100 rounded-full"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        updateFormData({ file: null, originalFileName: "" });
                                                                        setUIStates(prev => ({ ...prev, fileName: "" }));
                                                                        document.getElementById("file").value = "";
                                                                    }}
                                                                >
                                                                    <X className="h-3 w-3" />
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-2 text-sub-text-color">
                                                                <Upload className={contractCommonClasses.iconSize} />
                                                                <span className="text-sm">Choose File</span>
                                                            </div>
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={`grid grid-cols-2 sm:${contractCommonClasses.gridCols3} ${contractCommonClasses.gap4}`}>
                                        <BusinessInput
                                            fieldID="charges"
                                            label="Charges"
                                            placeholder="e.g. 12,000"
                                            value={formData.charges}
                                            onChange={handleBusinessValueChange}
                                            required={false}
                                            maxDecimalPlaces={2}
                                            maxIntegerDigits={9}
                                            className={contractCommonClasses.spacing}
                                            disabled={false}
                                        />

                                        <BusinessInput
                                            fieldID="renewal"
                                            label="Renewal"
                                            placeholder="e.g. 25,000"
                                            value={formData.renewal}
                                            onChange={handleBusinessValueChange}
                                            required={false}
                                            maxDecimalPlaces={2}
                                            maxIntegerDigits={9}
                                            className={contractCommonClasses.spacing}
                                            disabled={false}
                                        />

                                        <div className={`${contractCommonClasses.spacing} hidden sm:block`}>
                                            {/* Empty space - hidden on mobile, visible on desktop */}
                                        </div>
                                    </div>
                                    {renderFollowUpSection()}
                                    {/* Notes Section */}
                                    <div className={contractCommonClasses.spacing}>
                                        <Label htmlFor="notes">Notes</Label>
                                        <Textarea
                                            id="notes"
                                            value={formData.notes}
                                            onChange={(e) => updateFormData({ notes: e.target.value })}
                                            placeholder="e.g. Contract includes maintenance and support"
                                            className="text-sm transition-all duration-200 bg-white sahi-input resize-none"
                                            rows={2}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="sm:px-0 sm:sticky sm:bottom-0 sm:bg-background border-t sm:border-t-0 pb-6 sm:pb-3">
                            <div className="flex flex-col gap-4 sm:flex-row sm:justify-start sm:gap-3 w-full">

                                {/* Mobile Layout - Save full width only */}
                                <div className="flex sm:hidden w-full">
                                    <Button onClick={handleSubmit} className="sahi-primary-button w-full">
                                        <Check className="h-4 w-4" />
                                        {isCancellationMode ? 'Cancel' : (isRenewalMode ? 'Save' : (contractID ? 'Update' : 'Save'))} Contract
                                    </Button>
                                </div>

                                {/* Desktop Layout - Save + Close */}
                                <div className="hidden sm:flex sm:gap-3">
                                    <Button onClick={handleSubmit} className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[140px]">
                                        <Check className="h-4 w-4" />
                                        {isCancellationMode ? 'Cancel' : (isRenewalMode ? 'Save' : (contractID ? 'Update' : 'Save'))} Contract
                                    </Button>
                                    <Button variant="outline" tabIndex={-1} onClick={handleClose} className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]">
                                        <X className="h-5 w-5" />
                                        <span>Close</span>
                                    </Button>
                                </div>
                            </div>
                        </div>

                    </div>
                </DialogContent>
            </Dialog>

            {/* Masters Dialog */}
            {uiStates.masterDialog && (
                <MastersDialog
                    open={uiStates.masterDialog}
                    onClose={() => {
                        setUIStates(prev => ({ ...prev, masterDialog: false, masterSearchText: '' }));
                        setMasterDialogType(null);
                        setSelectedField("");
                    }}
                    masterType={masterDialogType}
                    directAdd={true}
                    openEntry={true}
                    prefilledName={uiStates.masterSearchText}
                    onProductAdded={(itemName) => {
                        if (selectedField) {
                            updateFormData({ [selectedField]: itemName });
                            // toast.success(`${itemName} added.`);
                        }
                        setUIStates(prev => ({ ...prev, masterDialog: false, masterSearchText: '' }));
                        setMasterDialogType(null);
                        setSelectedField("");
                    }}
                />
            )}

            {/* Connection Entry Dialog */}
            <ConnectionEntryDialog
                triggerButton={false}
                open={uiStates.connectionDialog}
                onOpenChange={(open) => {
                    setUIStates(prev => ({ ...prev, connectionDialog: open }));
                    if (!open) {
                        setUIStates(prev => ({ ...prev, connectionSearchText: '' }));
                    }
                }}
                onConnectionAdded={handleConnectionAdded}
                initialConnType={formData.contractTypeSelection == 'sold' ? 'Customer' : 'Supplier'}
                prefilledData={uiStates.connectionSearchText ? { businessName: uiStates.connectionSearchText } : null}
                calledFrom="CTRC"
				isBasic={true}
            />

            {/* Delete Confirmation Dialog */}
            <AlertBox
                open={uiStates.deleteAlert}
                onOpenChange={(open) => setUIStates(prev => ({ ...prev, deleteAlert: open }))}
                title="Please Confirm"
                description="Are you sure you wish to delete this contract?"
                actions={[
                    {
                        label: "Cancel",
                        variant: "cancel",
                        value: "cancel",
                        className: "w-full sm:w-auto",
                        icon: <X className={contractCommonClasses.iconSize} />
                    },
                    {
                        label: "Delete Contract",
                        variant: "action",
                        value: "delete",
                        className: "w-full sm:w-auto",
                        icon: <Trash2 className={contractCommonClasses.iconSize} />
                    }
                ]}
                onAction={(actionValue) => {
                    if (actionValue === "delete") {
                        handleDeleteContract();
                    }
                }}
            />
        </>
    );
};

export default ContractEntry;